import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { documentsAPI, issuesAPI, taskRequestsAPI, tasksAPI } from '../../services/api';
import { fmtDate } from '../../utils/dateUtils';
import { fmtDuration, fromHM } from '../../utils/duration';
import { parseApiError } from '../../utils/errors';
import { taskSourceLabel } from '../../utils/taskSource';

const STATUS_OPTS = ['pending', 'in_progress', 'completed', 'cancelled'];
const REVIEW_COLORS = { not_reviewed: 'gray', review_required: 'orange', approved: 'green', rejected: 'red' };

export default function TaskDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [docs, setDocs] = useState([]);
  const [issues, setIssues] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [issueForm, setIssueForm] = useState({ title: '', description: '', urgency: 'medium' });
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [reqForm, setReqForm] = useState({ type: 'review', notes: '' });
  const [respondFor, setRespondFor] = useState(null);
  const [respondText, setRespondText] = useState('');
  const [completionNote, setCompletionNote] = useState('');
  const [noteError, setNoteError] = useState('');
  const [reviewRemarks, setReviewRemarks] = useState('');
  const [reviewSaving, setReviewSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [timeLogs, setTimeLogs] = useState([]);
  const [logForm, setLogForm] = useState({ h: '', m: '', date: new Date().toISOString().slice(0, 10), note: '' });
  const [logSaving, setLogSaving] = useState(false);
  const fileRef = useRef();
  const replaceRef = useRef();
  const replaceDocId = useRef(null);

  const load = async () => {
    const [t, d, i, r, tl] = await Promise.all([
      tasksAPI.get(id),
      documentsAPI.list(id),
      issuesAPI.list({ task_id: id }),
      taskRequestsAPI.list({ task_id: id }),
      tasksAPI.timeLogs(id).catch(() => ({ data: [] })),
    ]);
    setTask(t.data); setDocs(d.data); setIssues(i.data); setRequests(r.data); setTimeLogs(tl.data);
    setStatus(t.data.status);
    setCompletionNote(t.data.completion_note || '');
    setReviewRemarks(t.data.hr_remarks || '');
  };

  useEffect(() => { load().finally(() => setLoading(false)); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loggedTotal = timeLogs.reduce((a, l) => a + (l.hours || 0), 0);

  const saveStatus = async () => {
    if (status === 'completed') {
      if (!completionNote.trim()) { setNoteError('Please describe the work done to complete this task'); return; }
      if (loggedTotal <= 0) {
        setNoteError('Add your time in the Time Log below before marking this task completed');
        return;
      }
    }
    setNoteError('');
    setSaving(true);
    try {
      const { data } = await tasksAPI.update(id, { status, completion_note: completionNote.trim() || null });
      setTask(data);
      await load();
      setMsg('Task updated');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      alert(parseApiError(err, 'Failed to update task'));
    } finally { setSaving(false); }
  };

  const addTimeLog = async () => {
    const hours = fromHM(logForm.h, logForm.m);
    if (!hours) { alert('Enter the time spent'); return; }
    setLogSaving(true);
    try {
      await tasksAPI.addTimeLog(id, { hours, work_date: logForm.date, note: logForm.note.trim() || null });
      setLogForm({ h: '', m: '', date: logForm.date, note: '' });
      await load();
    } catch (err) {
      alert(parseApiError(err, 'Failed to log time'));
    } finally { setLogSaving(false); }
  };

  const removeTimeLog = async (logId) => {
    if (!window.confirm('Remove this time entry?')) return;
    try { await tasksAPI.deleteTimeLog(id, logId); await load(); }
    catch (err) { alert(parseApiError(err, 'Failed to remove entry')); }
  };

  const respondRequest = async (r, status) => {
    try {
      await taskRequestsAPI.update(r.id, { status, hr_response: respondText.trim() || null });
      setRespondFor(null); setRespondText('');
      const { data } = await taskRequestsAPI.list({ task_id: id });
      setRequests(data);
    } catch (err) { alert(parseApiError(err, 'Failed to respond')); }
  };

  const submitReview = async (review_status) => {
    setReviewSaving(true);
    try {
      const payload = { review_status };
      if (reviewRemarks.trim()) payload.hr_remarks = reviewRemarks.trim();
      const { data } = await tasksAPI.hrUpdate(id, payload);
      setTask(data);
      setMsg(`Task marked "${review_status.replace('_', ' ')}" — the employee has been notified.`);
      setTimeout(() => setMsg(''), 4000);
    } catch (err) {
      alert(parseApiError(err, 'Failed to submit review'));
    } finally { setReviewSaving(false); }
  };

  const deleteTask = async () => {
    if (!window.confirm(`Delete task "${task.title}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await tasksAPI.remove(id);
      navigate(user.role === 'employee' ? '/employee/tasks' : '/hr/tasks');
    } catch (err) {
      alert(parseApiError(err, 'Failed to delete task'));
      setSaving(false);
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    await documentsAPI.upload(id, fd);
    const { data } = await documentsAPI.list(id);
    setDocs(data);
  };

  const blobFrom = (res) => {
    const type = res.headers?.['content-type'] || res.data?.type || 'application/octet-stream';
    return new Blob([res.data], { type });
  };

  const viewDoc = async (d) => {
    try {
      const res = await documentsAPI.download(d.id);
      const url = URL.createObjectURL(blobFrom(res));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { alert(parseApiError(err, 'Could not open document')); }
  };

  const downloadDoc = async (d) => {
    try {
      const res = await documentsAPI.download(d.id);
      const url = URL.createObjectURL(blobFrom(res));
      const a = document.createElement('a');
      a.href = url; a.download = d.original_filename || 'document'; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert(parseApiError(err, 'Download failed')); }
  };

  const startReplace = (d) => { replaceDocId.current = d.id; replaceRef.current.click(); };

  const onReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !replaceDocId.current) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      await documentsAPI.replace(replaceDocId.current, fd);
      const { data } = await documentsAPI.list(id);
      setDocs(data);
      setMsg('Document replaced');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) { alert(parseApiError(err, 'Replace failed')); }
  };

  const submitIssue = async (e) => {
    e.preventDefault();
    await issuesAPI.create({ ...issueForm, task_id: parseInt(id) });
    setShowIssueForm(false);
    setIssueForm({ title: '', description: '', urgency: 'medium' });
    const { data } = await issuesAPI.list({ task_id: id });
    setIssues(data);
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    await taskRequestsAPI.create({ task_id: parseInt(id), ...reqForm });
    setShowRequestForm(false);
    setReqForm({ type: 'review', notes: '' });
    const { data } = await taskRequestsAPI.list({ task_id: id });
    setRequests(data);
  };

  if (loading) return <div className="page-loading">Loading task...</div>;
  if (!task) return <div className="page">Task not found.</div>;

  const isOwner = task.assigned_to?.id === user.id || task.created_by?.id === user.id;
  const canEdit = isOwner && user.role === 'employee';
  // HR/super admin can delete any task; employees only ones they created.
  const canDelete = user.role !== 'employee' || task.created_by?.id === user.id;
  // Only HR/super admin review tasks and download documents
  const canReview = user.role === 'hr' || user.role === 'super_admin';
  const canDownloadDocs = user.role === 'hr' || user.role === 'super_admin';
  const REQ_STATUS_COLOR = { pending: 'orange', acknowledged: 'blue', resolved: 'green' };

  return (
    <div className="page">
      <div className="page-header">
        <h2>{task.title}</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {canDelete && (
            <button className="btn btn-danger" onClick={deleteTask} disabled={saving}>Delete Task</button>
          )}
          <button className="btn btn-ghost" onClick={() => navigate(-1)}>← Back</button>
        </div>
      </div>

      {msg && <div className="alert alert-success">{msg}</div>}

      <div className="detail-grid">
        <div className="detail-main">
          <div className="card">
            <div className="card-header"><h3>Task Details</h3></div>
            <div className="detail-meta">
              <div><span>Status</span><span className={`badge badge-${task.status}`}>{task.status.replace('_', ' ')}</span></div>
              <div><span>Priority</span><span className={`badge badge-${task.priority}`}>{task.priority}</span></div>
              <div><span>Source</span><span>{taskSourceLabel(task)}</span></div>
              {task.created_by && <div><span>Created by</span><span>{task.created_by.full_name}</span></div>}
              <div><span>Created</span><span>{fmtDate(task.created_at)}</span></div>
              <div><span>Review</span><span>{task.review_status.replace('_', ' ')}</span></div>
              {task.deadline && <div><span>Deadline</span><span className={task.is_delayed ? 'text-danger' : ''}>{fmtDate(task.deadline)}</span></div>}
              {task.expected_hours != null && <div><span>Expected</span><span>{fmtDuration(task.expected_hours)}</span></div>}
              {task.actual_hours != null && <div><span>Actual</span><span>{fmtDuration(task.actual_hours)}</span></div>}
              {task.status === 'completed' && task.end_time && (
                <div><span>Completed</span><span>{fmtDate(task.end_time)}</span></div>
              )}
            </div>
            {task.description && <p className="task-description">{task.description}</p>}
            {task.completion_note && (
              <div className="hr-remarks" style={{ marginTop: 10 }}>
                <strong>Work Done:</strong> {task.completion_note}
              </div>
            )}
            {task.hr_remarks && <div className="hr-remarks"><strong>HR Remarks:</strong> {task.hr_remarks}</div>}
          </div>

          {canEdit && (
            <div className="card mt-4">
              <div className="card-header"><h3>Update Progress</h3></div>
              <div style={{ padding: '0 20px 16px' }}>
                <div className="form-group">
                  <label>Status</label>
                  <select value={status} onChange={(e) => setStatus(e.target.value)}>
                    {STATUS_OPTS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                  </select>
                  <small className="field-hint">Total time logged on this task: {fmtDuration(loggedTotal)} · log time in the Time Log section below.</small>
                </div>
                <div className="form-group">
                  <label>Work Done {status === 'completed' && <span className="text-danger">*</span>}</label>
                  <textarea rows={3} value={completionNote}
                    placeholder="Describe the work you completed (required to submit — visible to HR & Admin)"
                    onChange={(e) => { setCompletionNote(e.target.value); setNoteError(''); }} />
                  {noteError && <span className="field-error">{noteError}</span>}
                </div>
                <button className="btn btn-primary" onClick={saveStatus} disabled={saving}>
                  {saving ? 'Saving...' : 'Update'}
                </button>
              </div>
            </div>
          )}

          {/* Time logs — log time spent each day (works while In Progress too) */}
          {canEdit && (
            <div className="card mt-4">
              <div className="card-header">
                <h3>Time Log</h3>
                <span className="text-muted" style={{ fontSize: 13 }}>Total: {fmtDuration(loggedTotal)}</span>
              </div>
              <div style={{ padding: '0 20px 16px' }}>
                <div className="form-row" style={{ alignItems: 'flex-end' }}>
                  <div className="form-group">
                    <label>Date</label>
                    <input type="date" value={logForm.date} onChange={(e) => setLogForm({ ...logForm, date: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Time spent</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input type="number" min="0" placeholder="Hours" value={logForm.h} onChange={(e) => setLogForm({ ...logForm, h: e.target.value })} />
                      <input type="number" min="0" max="59" placeholder="Minutes" value={logForm.m} onChange={(e) => setLogForm({ ...logForm, m: e.target.value })} />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label>Note (optional)</label>
                  <input value={logForm.note} placeholder="What did you work on?" onChange={(e) => setLogForm({ ...logForm, note: e.target.value })} />
                </div>
                <button className="btn btn-ghost" onClick={addTimeLog} disabled={logSaving}>{logSaving ? 'Adding…' : '+ Log Time'}</button>

                {timeLogs.length > 0 && (
                  <table className="data-table mt-4">
                    <thead><tr><th>Date</th><th>Time</th><th>Note</th><th></th></tr></thead>
                    <tbody>
                      {timeLogs.map((l) => (
                        <tr key={l.id}>
                          <td>{fmtDate(l.work_date, true)}</td>
                          <td>{fmtDuration(l.hours)}</td>
                          <td>{l.note || '—'}</td>
                          <td><button className="btn btn-sm btn-ghost" onClick={() => removeTimeLog(l.id)}>✕</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {canReview && (
            <div className="card mt-4">
              <div className="card-header">
                <h3>Review</h3>
                <span className={`badge badge-${REVIEW_COLORS[task.review_status] || 'gray'}`}>
                  {task.review_status.replace('_', ' ')}
                </span>
              </div>
              <div style={{ padding: '0 20px 16px' }}>
                <div className="form-group">
                  <label>Remarks (optional — shared with the employee)</label>
                  <textarea rows={2} value={reviewRemarks} placeholder="Feedback on this task…"
                    onChange={(e) => setReviewRemarks(e.target.value)} />
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-success" disabled={reviewSaving}
                    onClick={() => submitReview('approved')}>✓ Approve</button>
                  <button className="btn btn-danger" disabled={reviewSaving}
                    onClick={() => submitReview('rejected')}>✕ Reject</button>
                  <button className="btn btn-ghost" disabled={reviewSaving}
                    onClick={() => submitReview('review_required')}>Mark Review Required</button>
                  {task.review_status !== 'not_reviewed' && (
                    <button className="btn btn-ghost" disabled={reviewSaving}
                      onClick={() => submitReview('not_reviewed')}>Reset</button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="detail-side">
          {/* Requests (clarifications / meeting / review) — employees raise, HR/admin respond */}
          {(canEdit || canReview) && (
            <div className="card">
              <div className="card-header">
                <h3>Requests ({requests.length})</h3>
                {canEdit && (
                  <button className="btn btn-sm btn-ghost" onClick={() => setShowRequestForm(!showRequestForm)}>
                    {showRequestForm ? 'Cancel' : 'New Request'}
                  </button>
                )}
              </div>
              {showRequestForm && canEdit && (
                <form onSubmit={submitRequest} className="issue-form">
                  <div className="form-group">
                    <label>Type</label>
                    <select value={reqForm.type} onChange={(e) => setReqForm({ ...reqForm, type: e.target.value })}>
                      <option value="review">Task Review</option>
                      <option value="meeting">Meeting Request</option>
                      <option value="clarification">Clarification Needed</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea rows={3} value={reqForm.notes} onChange={(e) => setReqForm({ ...reqForm, notes: e.target.value })} />
                  </div>
                  <button type="submit" className="btn btn-primary btn-sm">Submit</button>
                </form>
              )}
              {requests.length === 0 && <p className="text-muted" style={{ padding: '0 20px 12px' }}>No requests.</p>}
              {requests.map((r) => (
                <div key={r.id} className="issue-item">
                  <div className="issue-title">
                    {r.type.replace('_', ' ')} <span className={`badge badge-${REQ_STATUS_COLOR[r.status]}`}>{r.status}</span>
                  </div>
                  {r.notes && <p className="text-muted" style={{ fontSize: 12 }}>{r.notes}</p>}
                  {r.hr_response && <div className="hr-response">Response: {r.hr_response}</div>}
                  {canReview && r.status === 'pending' && (
                    respondFor === r.id ? (
                      <div style={{ marginTop: 6 }}>
                        <textarea rows={2} value={respondText} placeholder="Reply to the employee…"
                          onChange={(e) => setRespondText(e.target.value)} style={{ width: '100%' }} />
                        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                          <button className="btn btn-sm btn-success" onClick={() => respondRequest(r, 'resolved')}>Resolve</button>
                          <button className="btn btn-sm btn-ghost" onClick={() => respondRequest(r, 'acknowledged')}>Acknowledge</button>
                          <button className="btn btn-sm btn-ghost" onClick={() => setRespondFor(null)}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button className="btn btn-sm btn-primary" style={{ marginTop: 6 }}
                        onClick={() => { setRespondFor(r.id); setRespondText(r.hr_response || ''); }}>Respond</button>
                    )
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Documents */}
          <div className="card mt-4">
            <div className="card-header">
              <h3>Documents ({docs.length})</h3>
              {isOwner && <button className="btn btn-sm btn-ghost" onClick={() => fileRef.current.click()}>Upload</button>}
            </div>
            <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={uploadFile} />
            <input ref={replaceRef} type="file" style={{ display: 'none' }} onChange={onReplaceFile} />
            {docs.length === 0 ? <p className="text-muted" style={{ padding: '0 20px 16px' }}>No documents.</p> : (
              <ul className="doc-list">
                {docs.map((d) => (
                  <li key={d.id}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span className="doc-name">{d.original_filename}</span>
                      <span className="doc-size">{(d.file_size / 1024).toFixed(1)} KB</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="btn btn-sm btn-ghost" onClick={() => viewDoc(d)}>View</button>
                      {canDownloadDocs && (
                        <button className="btn btn-sm btn-ghost" onClick={() => downloadDoc(d)}>Download</button>
                      )}
                      {isOwner && user.role === 'employee' && (
                        <button className="btn btn-sm btn-ghost" onClick={() => startReplace(d)}>Replace</button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Issues */}
          <div className="card mt-4">
            <div className="card-header">
              <h3>Issues ({issues.length})</h3>
              {isOwner && <button className="btn btn-sm btn-ghost" onClick={() => setShowIssueForm(!showIssueForm)}>
                {showIssueForm ? 'Cancel' : 'Raise Issue'}
              </button>}
            </div>
            {showIssueForm && (
              <form onSubmit={submitIssue} className="issue-form">
                <div className="form-group">
                  <label>Title</label>
                  <input required value={issueForm.title} onChange={(e) => setIssueForm({ ...issueForm, title: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea required rows={3} value={issueForm.description} onChange={(e) => setIssueForm({ ...issueForm, description: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Urgency</label>
                  <select value={issueForm.urgency} onChange={(e) => setIssueForm({ ...issueForm, urgency: e.target.value })}>
                    <option value="low">Low</option><option value="medium">Medium</option>
                    <option value="high">High</option><option value="critical">Critical</option>
                  </select>
                </div>
                <button type="submit" className="btn btn-primary btn-sm">Submit Issue</button>
              </form>
            )}
            {issues.map((iss) => (
              <div key={iss.id} className="issue-item">
                <div className="issue-title">{iss.title} <span className={`badge badge-${iss.urgency}`}>{iss.urgency}</span></div>
                <div className="issue-status">{iss.status}</div>
                {iss.hr_response && <div className="hr-response">HR: {iss.hr_response}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
